import React, { useState, useEffect, useCallback, useRef } from "react";
import { Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Locations from "./locations";
import Devices from "./devices";
import Sockets from "./sockets";
import News from "./news";
import Chat from "./chat";
import Cars from "./cars";
import Profile from "./profile";
import ThemeSelector from "../components/ThemeSelector";
import Clients from "./clients";
import OperatorInfo from "./operator-info";
import DeviceAccessOperator from "./device-access-operator";
import CameraAccessOperator from "./camera-access-operator";
import OperatorSensors from "./sensors";
import ClientPortalSettings from "./client-portal-settings";
import OperatorRequests from "./requests";
import OperatorBilling from "./billing";
import OperatorWidgets from "./widgets";
import OperatorPolls from "./polls";
import SupportOperator from "./support";
import { fetchCurrentOperator } from "../services/operators";
import { fetchOperatorSensorAccess } from "../services/sensors";
import { fetchOperatorNetworkFeatures } from "../services/network-features";
import { fetchClientAccessRequests } from "../services/clients";
import { listOperatorRequests } from "../services/requests";
import { useWebSocket } from "../context/WebSocketContext"
import { getMe, logout } from "../services/session";
import { fetchLicenseStatus } from "../services/license";
import { fetchOperatorNews, fetchUnreadOperatorNews } from "../services/news";
import { fetchUnreadOperatorInfo } from "../services/operator_info";
import { getToMeConversations } from "../services/chat";
import { fetchOperatorPolls, fetchUnreadOperatorPolls } from "../services/polls";
import { apiFetch } from "../utils/apiFetch";
import { setNativeAppBadge } from "../utils/nativeBadge";
import { loadWeatherTextForRibbon } from "../utils/weather";
import { pushAndroidBackHandler } from "../utils/androidBack";
import { notifyInAppSound, withInAppSound } from "../utils/notificationSound";
import { API_BASE_OPERATOR } from "../config/vars";

const OperatorPage = () => {
    const [selectedSection, setSelectedSection] = useState("dashboard");
    const [chatShellNav, setChatShellNav] = useState(null);
    const selectedSectionRef = useRef(selectedSection);
    const [operatorName, setOperatorName] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [openGroupId, setOpenGroupId] = useState(null);
    const [canSeeClients, setCanSeeClients] = useState(false);
    const [hasNews, setHasNews] = useState(false);
    const [unreadNewsCount, setUnreadNewsCount] = useState(0);
    const [hasOperatorInfo, setHasOperatorInfo] = useState(false);
    const [unreadOperatorInfoCount, setUnreadOperatorInfoCount] = useState(0);
    const [hasChats, setHasChats] = useState(false);
    const [unreadChatsCount, setUnreadChatsCount] = useState(0);
    const [hasPolls, setHasPolls] = useState(false);
    const [unreadPollsCount, setUnreadPollsCount] = useState(0);
    const [pendingClientApprovalsCount, setPendingClientApprovalsCount] = useState(0);
    const [openRequestsCount, setOpenRequestsCount] = useState(0);
    const [serverOffsetMs, setServerOffsetMs] = useState(null);
    const [serverTick, setServerTick] = useState(0);
    const [showTime, setShowTime] = useState(() => window.innerWidth >= 520);
    const [weatherText, setWeatherText] = useState(null);
    const [licenseStatus, setLicenseStatus] = useState(null);
    const [licenseReady, setLicenseReady] = useState(false);
    const [sensorAccess, setSensorAccess] = useState({ page_enabled: false, can_manage_network: false });
    const [networkMenuKeys, setNetworkMenuKeys] = useState(null); // null = not loaded yet → show all until loaded
    const [networkFeatureFlags, setNetworkFeatureFlags] = useState(null);
    const navigate = useNavigate();

    const navbarRef = useRef(null);
    const drawerRef = useRef(null);

    const networkId = parseInt(localStorage.getItem("networkId"));
    const operatorId = parseInt(localStorage.getItem("operatorId"));
    const { connect, subscribe, unsubscribe } = useWebSocket();

    const isLicenseInvalid = licenseReady && licenseStatus && licenseStatus.valid === false;

    const getMenuBadgeCount = (key) => {
        if (key === "clients") return pendingClientApprovalsCount;
        if (key === "requests") return openRequestsCount;
        if (key === "news") return unreadNewsCount;
        if (key === "polls") return unreadPollsCount;
        if (key === "operator-info") return unreadOperatorInfoCount;
        if (key === "chat") return unreadChatsCount;
        return 0;
    };

    useEffect(() => {
        const count =
            Math.max(0, unreadNewsCount) +
            Math.max(0, unreadOperatorInfoCount) +
            Math.max(0, unreadChatsCount) +
            Math.max(0, unreadPollsCount) +
            Math.max(0, Number(openRequestsCount) || 0) +
            Math.max(0, Number(pendingClientApprovalsCount) || 0);
        setNativeAppBadge(count);
    }, [unreadNewsCount, unreadOperatorInfoCount, unreadChatsCount, unreadPollsCount, openRequestsCount, pendingClientApprovalsCount]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                setMenuOpen(false);
                setOpenGroupId(null);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    useEffect(() => {
        if (!openGroupId) return;
        const onDocMouseDown = (e) => {
            const target = e.target;
            if (navbarRef.current && navbarRef.current.contains(target)) return;
            if (drawerRef.current && drawerRef.current.contains(target)) return;
            setOpenGroupId(null);
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [openGroupId]);

    const refreshUnreadOperatorNews = useCallback(async () => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!networkId) return;

        try {
            const unread = await fetchUnreadOperatorNews();
            const count = Math.max(0, Number(unread?.unread_count) || 0);
            setUnreadNewsCount(count);
            if (unread?.has_unread === true || count > 0) setHasNews(true);
            else if (unread?.has_unread === false || count === 0) setHasNews(false);
            return;
        } catch (_) {
            // fall back to list-based heuristic
        }

        try {
            const list = await fetchOperatorNews();
            if (!Array.isArray(list) || list.length === 0) return;

            const newestAt = list
                .map((x) => (x?.dtlm ? new Date(x.dtlm).getTime() : null))
                .filter((t) => typeof t === "number" && !Number.isNaN(t))
                .sort((a, b) => b - a)[0];

            if (!newestAt) return;

            const marker = localStorage.getItem("lastViewedOperatorNewsAt");
            if (!marker) {
                setUnreadNewsCount(Array.isArray(list) ? list.length : 0);
                setHasNews(true);
                return;
            }

            const seenAt = new Date(marker).getTime();
            if (!Number.isNaN(seenAt) && newestAt > seenAt) {
                const count = list.filter((item) => {
                    const itemTs = item?.dtlm ? new Date(item.dtlm).getTime() : NaN;
                    return Number.isFinite(itemTs) && itemTs > seenAt;
                }).length;
                setUnreadNewsCount(count);
                setHasNews(true);
            } else {
                setUnreadNewsCount(0);
                setHasNews(false);
            }
        } catch (_) {
            // ignore
        }
    }, [licenseReady, isLicenseInvalid, networkId]);

    const refreshUnreadChats = useCallback(async () => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!operatorId) return;
        try {
            const convs = await getToMeConversations();
            if (Array.isArray(convs)) {
                const unreadCount = convs.reduce(
                    (sum, conversation) => sum + Math.max(0, Number(conversation?.unread_count) || 0),
                    0
                );
                setUnreadChatsCount(unreadCount);
                setHasChats(unreadCount > 0);
            }
        } catch (_) {
            // ignore
        }
    }, [licenseReady, isLicenseInvalid, operatorId]);

    const refreshOperatorInfoBadge = useCallback(async () => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!networkId) return;

        try {
            const unread = await fetchUnreadOperatorInfo();
            const count = Math.max(0, Number(unread?.unread_count) || 0);
            setUnreadOperatorInfoCount(count);
            if (unread?.has_unread === true || count > 0) setHasOperatorInfo(true);
            else if (unread?.has_unread === false || count === 0) setHasOperatorInfo(false);
            return;
        } catch (_) {
            // fall back to list-based heuristic
        }

        try {
            const res = await apiFetch(`${API_BASE_OPERATOR}/operator-info/operator`);
            if (!res.ok) return;
            const data = await res.json();
            if (!Array.isArray(data) || data.length === 0) return;

            const newestAt = data
                .map((x) => (x?.dtlm ? new Date(x.dtlm).getTime() : null))
                .filter((t) => typeof t === "number" && !Number.isNaN(t))
                .sort((a, b) => b - a)[0];

            if (!newestAt) return;

            const marker = localStorage.getItem("lastViewedOperatorInfoAt");
            if (!marker) {
                setUnreadOperatorInfoCount(Array.isArray(data) ? data.length : 0);
                setHasOperatorInfo(true);
                return;
            }

            const seenAt = new Date(marker).getTime();
            if (!Number.isNaN(seenAt) && newestAt > seenAt) {
                const count = data.filter((item) => {
                    const itemTs = item?.dtlm ? new Date(item.dtlm).getTime() : NaN;
                    return Number.isFinite(itemTs) && itemTs > seenAt;
                }).length;
                setUnreadOperatorInfoCount(count);
                setHasOperatorInfo(true);
            } else {
                setUnreadOperatorInfoCount(0);
                setHasOperatorInfo(false);
            }
        } catch (_) {
            // ignore
        }
    }, [licenseReady, isLicenseInvalid, networkId]);

    const refreshUnreadOperatorPolls = useCallback(async () => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!networkId) return;

        try {
            const unread = await fetchUnreadOperatorPolls();
            const count = Math.max(0, Number(unread?.unread_count) || 0);
            setUnreadPollsCount(count);
            if (unread?.has_unread === true || count > 0) setHasPolls(true);
            else if (unread?.has_unread === false || count === 0) setHasPolls(false);
            return;
        } catch (_) {
            // fall back to list-based heuristic
        }

        try {
            const list = await fetchOperatorPolls();
            if (!Array.isArray(list) || list.length === 0) return;

            const newestAt = list
                .map((x) => (x?.dtlm ? new Date(x.dtlm).getTime() : null))
                .filter((t) => typeof t === "number" && !Number.isNaN(t))
                .sort((a, b) => b - a)[0];

            if (!newestAt) return;

            const marker = localStorage.getItem("lastViewedOperatorPollsAt");
            if (!marker) {
                setUnreadPollsCount(Array.isArray(list) ? list.length : 0);
                setHasPolls(true);
                return;
            }

            const seenAt = new Date(marker).getTime();
            if (!Number.isNaN(seenAt) && newestAt > seenAt) {
                const count = list.filter((item) => {
                    const itemTs = item?.dtlm ? new Date(item.dtlm).getTime() : NaN;
                    return Number.isFinite(itemTs) && itemTs > seenAt;
                }).length;
                setUnreadPollsCount(count);
                setHasPolls(true);
            } else {
                setUnreadPollsCount(0);
                setHasPolls(false);
            }
        } catch (_) {
            // ignore
        }
    }, [licenseReady, isLicenseInvalid, networkId]);

    const refreshPendingClientApprovals = useCallback(async () => {
        if (!licenseReady) return;
        if (isLicenseInvalid) {
            setPendingClientApprovalsCount(0);
            return;
        }

        try {
            const requests = await fetchClientAccessRequests();
            setPendingClientApprovalsCount(Array.isArray(requests) ? requests.length : 0);
        } catch (_) {
            // ignore temporary backend/network errors
        }
    }, [licenseReady, isLicenseInvalid]);

    const refreshOpenRequestsCount = useCallback(async () => {
        if (!licenseReady) return;
        if (isLicenseInvalid) {
            setOpenRequestsCount(0);
            return;
        }

        try {
            const requests = await listOperatorRequests({ status: "open" });
            setOpenRequestsCount(Array.isArray(requests) ? requests.length : 0);
        } catch (_) {
            // ignore temporary backend/network errors
        }
    }, [licenseReady, isLicenseInvalid]);

    // Subscribe to news (both admin and operator news)
    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!networkId)
            return;

        const roomId = `network:${networkId}`;
        const handleNewsNew = withInAppSound(() => refreshUnreadOperatorNews());

        // NewsAdmins - news created by admin
        connect("news", roomId);

        subscribe("news", roomId, "news_new", handleNewsNew);

        return () => {
            unsubscribe("news", roomId, "news_new", handleNewsNew);
        };

    }, [connect, subscribe, unsubscribe, networkId, licenseReady, isLicenseInvalid, refreshUnreadOperatorNews]);

    // Subscribe to operator info (created by any operator in network)
    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!networkId)
            return;

        const roomId = `network:${networkId}`;
        const handleOperatorInfoNew = withInAppSound(() => refreshOperatorInfoBadge());

        connect("operator_info", roomId);

        subscribe("operator_info", roomId, "operator_info_new", handleOperatorInfoNew);

        return () => {
            unsubscribe("operator_info", roomId, "operator_info_new", handleOperatorInfoNew);
        };

    }, [connect, subscribe, unsubscribe, networkId, licenseReady, isLicenseInvalid, refreshOperatorInfoBadge]);

    // Subscribe to chat updates
    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!operatorId)
            return;

        const roomId = `operator:${operatorId}`;
        const handleChatNew = () => {
            if (selectedSectionRef.current !== "chat") {
                notifyInAppSound();
            }
            refreshUnreadChats();
        };

        const handleChatHighlighted = (payload) => {
            if (payload && payload.head_id) {
                refreshUnreadChats();
            }
        };

        connect("chat", roomId);

        subscribe("chat", roomId, "chat_new", handleChatNew);

        subscribe("chat", roomId, "chat_highlighted", handleChatHighlighted);

        return () => {
            unsubscribe("chat", roomId, "chat_new", handleChatNew);
            unsubscribe("chat", roomId, "chat_highlighted", handleChatHighlighted);
        };

    }, [connect, subscribe, unsubscribe, operatorId, licenseReady, isLicenseInvalid, refreshUnreadChats]);

    // Subscribe to polls updates (created in network)
    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!networkId) return;

        const roomId = `network:${networkId}`;
        const handlePollNew = withInAppSound(() => refreshUnreadOperatorPolls());
        const handlePollVote = () => window.dispatchEvent(new Event("wr_polls_changed"));

        connect("polls", roomId);

        subscribe("polls", roomId, "poll_new", handlePollNew);

        subscribe("polls", roomId, "poll_vote", handlePollVote);

        return () => {
            unsubscribe("polls", roomId, "poll_new", handlePollNew);
            unsubscribe("polls", roomId, "poll_vote", handlePollVote);
        };
    }, [connect, subscribe, unsubscribe, networkId, licenseReady, isLicenseInvalid, refreshUnreadOperatorPolls]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!networkId) return;

        const roomId = `network:${networkId}`;
        const handleAccessRequestChanged = withInAppSound(() => {
            refreshPendingClientApprovals();
        });

        connect("client_access_requests", roomId);
        subscribe("client_access_requests", roomId, "client_access_request_changed", handleAccessRequestChanged);

        return () => {
            unsubscribe("client_access_requests", roomId, "client_access_request_changed", handleAccessRequestChanged);
        };
    }, [connect, subscribe, unsubscribe, networkId, licenseReady, isLicenseInvalid, refreshPendingClientApprovals]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;
        if (!networkId) return;

        const roomId = `network:${networkId}`;
        const handleRequestChanged = withInAppSound(() => {
            refreshOpenRequestsCount();
        });

        connect("requests", roomId);
        subscribe("requests", roomId, "request_changed", handleRequestChanged);

        return () => {
            unsubscribe("requests", roomId, "request_changed", handleRequestChanged);
        };
    }, [connect, subscribe, unsubscribe, networkId, licenseReady, isLicenseInvalid, refreshOpenRequestsCount]);

    useEffect(() => {
        const onPush = (event) => {
            const kind = String(event?.detail?.kind || "").trim();
            if (["news", "news_item", "announcement"].includes(kind)) {
                refreshUnreadOperatorNews();
            }
            if (["poll", "polls", "survey"].includes(kind)) {
                refreshUnreadOperatorPolls();
            }
            if (["operator_info", "operator-info", "info", "information"].includes(kind)) {
                refreshOperatorInfoBadge();
            }
            if (["chat", "message", "messages"].includes(kind)) {
                refreshUnreadChats();
            }
            if (["access_request", "client_access_request"].includes(kind)) {
                refreshPendingClientApprovals();
            }
            if (["request", "request_response", "request_reply"].includes(kind)) {
                refreshOpenRequestsCount();
            }
        };

        window.addEventListener("wrPushReceived", onPush);
        return () => window.removeEventListener("wrPushReceived", onPush);
    }, [
        refreshUnreadOperatorNews,
        refreshUnreadOperatorPolls,
        refreshOperatorInfoBadge,
        refreshUnreadChats,
        refreshPendingClientApprovals,
        refreshOpenRequestsCount,
    ]);

    // On load, decide whether badges should be visible (covers page reloads).
    useEffect(() => {
        refreshUnreadOperatorNews();
        refreshOperatorInfoBadge();
        refreshUnreadChats();
        refreshUnreadOperatorPolls();
        refreshPendingClientApprovals();
        refreshOpenRequestsCount();
    }, [refreshUnreadOperatorNews, refreshOperatorInfoBadge, refreshUnreadChats, refreshUnreadOperatorPolls, refreshPendingClientApprovals, refreshOpenRequestsCount]);

    useEffect(() => {
        if (!licenseReady || isLicenseInvalid) return;

        const onFocus = () => {
            refreshPendingClientApprovals();
            refreshOpenRequestsCount();
        };
        const onRequestsChanged = (event) => {
            const count = Number(event?.detail?.count);
            if (Number.isFinite(count)) {
                setPendingClientApprovalsCount(count);
                return;
            }
            refreshPendingClientApprovals();
        };
        const intervalId = window.setInterval(() => {
            refreshPendingClientApprovals();
            refreshOpenRequestsCount();
        }, 60000);

        window.addEventListener("focus", onFocus);
        window.addEventListener("wr_operator_client_access_requests_changed", onRequestsChanged);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("wr_operator_client_access_requests_changed", onRequestsChanged);
        };
    }, [licenseReady, isLicenseInvalid, refreshPendingClientApprovals, refreshOpenRequestsCount]);

    useEffect(() => {
        const loadMe = async () => {
            const me = await getMe();
            if (!me) return navigate("/");
            setOperatorName(me.name || "");

            const serverMs = Date.parse(me.server_time_utc);
            if (!Number.isNaN(serverMs)) {
                setServerOffsetMs(serverMs - Date.now());
            }

            let ls = null;
            try {
                ls = await fetchLicenseStatus();
                setLicenseStatus(ls);
            } catch (_) {
                // If status can't be loaded, don't block UI (backend might be restarting).
                setLicenseStatus(null);
            } finally {
                setLicenseReady(true);
            }

            // If license is invalid, avoid calling business APIs (they will return 402).
            if (ls && ls.valid === false) {
                return;
            }

            const loadOperator = async () => {
                try {
                    const op = await fetchCurrentOperator();
                    setCanSeeClients(op.can_see_clients);
                } catch (err) {
                    // If license is missing/expired, backend will answer 402 on business endpoints.
                    if (err && err.status === 402) {
                        setLicenseStatus({ valid: false, detail: err.message || "Термін ліцензії закінчився" });
                        setLicenseReady(true);
                        return;
                    }

                    console.error("Не вдалося отримати дані оператора", err);
                    navigate("/");
                }
            };

            loadOperator();
        };

        loadMe();
    }, [navigate]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const text = await loadWeatherTextForRibbon();
                if (!cancelled) setWeatherText(text);
            } catch (_) {
                if (!cancelled) setWeatherText(null);
            }
        };

        load();

        const handler = () => load();
        window.addEventListener("wrWeatherChanged", handler);
        return () => {
            cancelled = true;
            window.removeEventListener("wrWeatherChanged", handler);
        };
    }, []);

    // If any API call hits 402 later (license expired while running), refresh status.
    useEffect(() => {
        const onBlocked = async () => {
            try {
                const ls = await fetchLicenseStatus();
                setLicenseStatus(ls);
                setLicenseReady(true);
            } catch (_) {
                setLicenseStatus({ valid: false, detail: "Термін ліцензії закінчився" });
                setLicenseReady(true);
            }
        };
        window.addEventListener("licenseBlocked", onBlocked);
        return () => window.removeEventListener("licenseBlocked", onBlocked);
    }, []);

    // While warning/blocked is visible, re-check so the banner clears after renew without reload.
    useEffect(() => {
        if (!licenseReady) return undefined;
        const needsWatch =
            (licenseStatus && licenseStatus.valid === false)
            || (licenseStatus && licenseStatus.valid && Number.isFinite(licenseStatus.days_left) && licenseStatus.days_left <= 15);
        if (!needsWatch) return undefined;
        const timer = window.setInterval(async () => {
            try {
                const ls = await fetchLicenseStatus();
                setLicenseStatus(ls);
            } catch (_) {
                // ignore
            }
        }, 60000);
        return () => window.clearInterval(timer);
    }, [licenseReady, licenseStatus]);

    useEffect(() => {
        const loadSensorAccess = async () => {
            if (!networkId) {
                setSensorAccess({ page_enabled: false, can_manage_network: false });
                return;
            }

            try {
                const access = await fetchOperatorSensorAccess();
                setSensorAccess({
                    page_enabled: access?.page_enabled === true,
                    can_manage_network: access?.can_manage_network === true,
                });
            } catch (_) {
                setSensorAccess({ page_enabled: false, can_manage_network: false });
            }
        };

        const loadNetworkFeatures = async () => {
            if (!networkId) {
                setNetworkMenuKeys(null);
                setNetworkFeatureFlags(null);
                return;
            }
            try {
                const data = await fetchOperatorNetworkFeatures();
                const keys = Array.isArray(data?.operator_menu_keys) ? data.operator_menu_keys : [];
                setNetworkMenuKeys(keys);
                setNetworkFeatureFlags(data?.features && typeof data.features === "object" ? data.features : {});
            } catch (_) {
                setNetworkMenuKeys(null);
                setNetworkFeatureFlags(null);
            }
        };

        loadSensorAccess();
        loadNetworkFeatures();
    }, [networkId]);

    useEffect(() => {
        const onResize = () => setShowTime(window.innerWidth >= 520);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (serverOffsetMs === null) return;
        const id = setInterval(() => setServerTick((t) => t + 1), 60 * 1000);
        return () => clearInterval(id);
    }, [serverOffsetMs]);

    const formatKyiv = (d, withTime) => {
        if (!d) return "";
        const opts = {
            timeZone: "Europe/Kyiv",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
        };
        return new Intl.DateTimeFormat("uk-UA", opts).format(d).replace(",", "");
    };

    const serverNow = serverOffsetMs === null ? null : new Date(Date.now() + serverOffsetMs);
    // eslint-disable-next-line no-unused-vars
    const _serverTick = serverTick;
    const serverDateText = serverNow ? formatKyiv(serverNow, false) : "";
    const serverDateTimeText = serverNow ? formatKyiv(serverNow, true) : "";

    const warningDaysLeft = (licenseStatus && licenseStatus.valid && Number.isFinite(licenseStatus.days_left))
        ? licenseStatus.days_left
        : null;
    const showLicenseWarning = !isLicenseInvalid && warningDaysLeft !== null && warningDaysLeft <= 15;

    const menuItems = {
        dashboard: { name: "Головна", key: "dashboard", icon: "🏠" },
        locations: { name: "Локації", key: "locations", icon: "📍" },
        sockets: { name: "Модулі СмартДоступ", key: "sockets", icon: "🔌" },
        devices: { name: "Пристрої", key: "devices", icon: "💻" },
        sensors: { name: "Сенсори", key: "sensors", icon: "🚨" },
        doorAccess: { name: "Відкрити замок", key: "door-access", icon: "🔑" },
        cameraAccess: { name: "Камери", key: "camera-access", icon: "📹" },
        clients: { name: "Клієнти", key: "clients", icon: "👥" },
        chat: { name: "Переписки", key: "chat", icon: "💬" },
        requests: { name: "Заявки", key: "requests", icon: "📝" },
        cars: { name: "Авто", key: "cars", icon: "🚗" },
        polls: { name: "Опитування", key: "polls", icon: "🗳️" },
        news: { name: "Новини", key: "news", icon: "📰" },
        operatorInfo: { name: "Інформація", key: "operator-info", icon: "ℹ️" },
        billing: { name: "Оплати", key: "billing", icon: "💳" },
        clientPortal: { name: "Налаштування клієнта", key: "client-portal", icon: "🧩" },
        profile: { name: "Профіль", key: "profile", icon: "⚙️" },
        support: { name: "Служба підтримки", key: "support", icon: "📞" },
        logout: { name: "Вихід", key: "logout", icon: "🚪" },
    };

    const topItems = [];

    const ALWAYS_OPERATOR_KEYS = new Set(["dashboard", "profile", "support", "logout"]);

    const isNetworkModuleAllowed = (key) => {
        if (ALWAYS_OPERATOR_KEYS.has(key)) return true;
        if (networkMenuKeys === null) return true; // not loaded / fail-open
        if (key === "sensors") {
            return networkMenuKeys.includes("sensors") && sensorAccess.page_enabled;
        }
        return networkMenuKeys.includes(key);
    };

    const filterMenuItems = (items) => (items || []).filter((item) => isNetworkModuleAllowed(item.key));

    const menuGroups = isLicenseInvalid
        ? []
        : [
            {
                id: "infra",
                title: "Інфраструктура",
                items: filterMenuItems([
                    menuItems.locations,
                    menuItems.sockets,
                    menuItems.devices,
                    menuItems.sensors,
                    menuItems.doorAccess,
                    menuItems.cameraAccess,
                    ...((canSeeClients || pendingClientApprovalsCount > 0) ? [menuItems.clients] : []),
                ]),
            },
            {
                id: "comm",
                title: "Комунікація",
                items: filterMenuItems([menuItems.chat, menuItems.requests, menuItems.cars, menuItems.polls]),
            },
            {
                id: "content",
                title: "Контент",
                items: filterMenuItems([menuItems.news, menuItems.operatorInfo]),
            },
            {
                id: "finance",
                title: "Фінанси, Аналітика",
                items: filterMenuItems([menuItems.billing]),
            },
            {
                id: "settings",
                title: "Налаштування",
                items: filterMenuItems([menuItems.clientPortal, menuItems.profile, menuItems.support]),
            },
        ].filter((group) => group.items.length > 0);

    // Logout must stay available even when the license blocks other sections.
    const bottomItems = [menuItems.logout];

    // If current section was disabled for this network — return to dashboard.
    useEffect(() => {
        if (networkMenuKeys === null || isLicenseInvalid) return;
        const alwaysOn = new Set(["dashboard", "profile", "support", "logout"]);
        if (alwaysOn.has(selectedSection)) return;
        let allowed = true;
        if (selectedSection === "sensors") {
            allowed = networkMenuKeys.includes("sensors") && sensorAccess.page_enabled;
        } else {
            allowed = networkMenuKeys.includes(selectedSection);
        }
        if (!allowed) {
            setSelectedSection("dashboard");
        }
    }, [networkMenuKeys, selectedSection, isLicenseInvalid, sensorAccess.page_enabled]);

    const pageTitles = {
        dashboard: "Головна",
        locations: "Локації",
        devices: "Пристрої",
        sensors: "Сенсори",
        sockets: "Модулі СмартДоступ",
        "door-access": "Відкрити замок",
        "camera-access": "Камери та відеоархів",
        news: "Новини",
        polls: "Опитування",
        "operator-info": "Інформація",
        chat: "Чат",
        cars: "Авто",
        clients: "Клієнти",
        requests: "Заявки клієнтів",
        billing: "Оплати",
        "client-portal": "Налаштування сторінки клієнта",
        profile: "Профіль",
        support: "Служба підтримки",
    };

    const currentPageTitle = selectedSection === "dashboard" ? null : (pageTitles[selectedSection] || null);
    const displayPageTitle = chatShellNav?.focused
        ? chatShellNav.title
        : currentPageTitle;

    const handleSectionClick = useCallback(async (key) => {
        setMenuOpen(false);
        setOpenGroupId(null);
        if (key === "logout") {
            try {
                await logout();
            } catch (_) {
                // ignore — always clear local session
            }
            try {
                localStorage.clear();
            } catch (_) {
                // ignore
            }
            navigate("/");
            return;
        }
        if (isLicenseInvalid) {
            return;
        }
        setSelectedSection(key);
    }, [navigate, isLicenseInvalid]);

    const goHome = useCallback(() => {
        if (isLicenseInvalid) return;
        handleSectionClick("dashboard");
    }, [handleSectionClick, isLicenseInvalid]);

    useEffect(() => {
        selectedSectionRef.current = selectedSection;
    }, [selectedSection]);

    useEffect(() => {
        if (selectedSection !== "chat") {
            setChatShellNav(null);
        }
    }, [selectedSection]);

    useEffect(() => {
        return pushAndroidBackHandler(() => {
            if (isLicenseInvalid) return false;
            if (chatShellNav?.focused) {
                chatShellNav.back?.();
                return true;
            }
            if (menuOpen) {
                setMenuOpen(false);
                return true;
            }
            if (openGroupId) {
                setOpenGroupId(null);
                return true;
            }
            if (selectedSection !== "dashboard") {
                handleSectionClick("dashboard");
                return true;
            }
            return false;
        });
    }, [chatShellNav, menuOpen, openGroupId, selectedSection, handleSectionClick, isLicenseInvalid]);

    const consumePendingPushOpen = useCallback(() => {
        if (isLicenseInvalid) return;

        let raw = null;
        try {
            raw = localStorage.getItem("WR_OPERATOR_PENDING_PUSH_OPEN");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        let parsed = null;
        try {
            parsed = JSON.parse(raw);
        } catch (_) {
            try {
                localStorage.removeItem("WR_OPERATOR_PENDING_PUSH_OPEN");
            } catch (_) {
                // ignore
            }
            return;
        }

        const kind = (parsed?.kind || "").toString().trim();
        if (!kind) {
            try {
                localStorage.removeItem("WR_OPERATOR_PENDING_PUSH_OPEN");
            } catch (_) {
                // ignore
            }
            return;
        }

        const openSection = (section) => {
            setMenuOpen(false);
            setOpenGroupId(null);
            setSelectedSection(section);
            return true;
        };

        let opened = false;

        if (kind === "chat") {
            const headId = parseInt(parsed?.head_id);
            if (Number.isFinite(headId) && headId > 0) {
                try {
                    localStorage.setItem("pendingChatOpenHeadId_operator", String(headId));
                } catch (_) {
                    // ignore
                }
            }
            opened = openSection("chat");
        } else if (kind === "news") {
            const newsId = parseInt(parsed?.news_id);
            if (Number.isFinite(newsId) && newsId > 0) {
                try {
                    localStorage.setItem("pendingNewsOpenId_operator", String(newsId));
                } catch (_) {
                    // ignore
                }
            }
            opened = openSection("news");
        } else if (kind === "sensor_alert") {
            const sensorId = parseInt(parsed?.sensor_id);
            const eventId = parseInt(parsed?.event_id);
            if (Number.isFinite(sensorId) && sensorId > 0) {
                try {
                    localStorage.setItem("pendingSensorOpenId_operator", String(sensorId));
                } catch (_) {
                    // ignore
                }
            }
            if (Number.isFinite(eventId) && eventId > 0) {
                try {
                    localStorage.setItem("pendingSensorEventOpenId_operator", String(eventId));
                } catch (_) {
                    // ignore
                }
            }
            opened = openSection("sensors");
        } else if (kind === "operator_info") {
            const infoId = parseInt(parsed?.info_id);
            if (Number.isFinite(infoId) && infoId > 0) {
                try {
                    localStorage.setItem("pendingOperatorInfoOpenId_operator", String(infoId));
                } catch (_) {
                    // ignore
                }
            }
            opened = openSection("operator-info");
        } else if (kind === "poll") {
            const pollId = parseInt(parsed?.poll_id);
            if (Number.isFinite(pollId) && pollId > 0) {
                try {
                    localStorage.setItem("pendingPollOpenId_operator", String(pollId));
                } catch (_) {
                    // ignore
                }
            }
            opened = openSection("polls");
        } else if (["request", "request_response", "request_reply"].includes(kind)) {
            const requestId = parseInt(parsed?.request_id);
            if (Number.isFinite(requestId) && requestId > 0) {
                try {
                    localStorage.setItem("pendingRequestOpenId_operator", String(requestId));
                } catch (_) {
                    // ignore
                }
            }
            opened = openSection("requests");
        } else if (["access_request", "client_access_request"].includes(kind)) {
            const requestId = parseInt(parsed?.access_request_id || parsed?.request_id);
            if (Number.isFinite(requestId) && requestId > 0) {
                try {
                    localStorage.setItem("pendingClientAccessRequestOpenId_operator", String(requestId));
                } catch (_) {
                    // ignore
                }
            }
            opened = openSection("clients");
        }

        if (opened) {
            try {
                localStorage.removeItem("WR_OPERATOR_PENDING_PUSH_OPEN");
            } catch (_) {
                // ignore
            }
        }
    }, [isLicenseInvalid]);

    useEffect(() => {
        if (!licenseReady) return;
        consumePendingPushOpen();
        const handler = () => consumePendingPushOpen();
        window.addEventListener("wrPushOpen", handler);
        return () => window.removeEventListener("wrPushOpen", handler);
    }, [consumePendingPushOpen, licenseReady]);

    return (
        <>
            <div className={`header-bar ${chatShellNav?.focused ? "header-bar--subview" : ""}`}>
                <div className="header-left">
                    <div className="header-greeting">
                        <div className="header-greeting-top">
                            {selectedSection !== "dashboard" ? (
                                <button
                                    type="button"
                                    className="header-home-btn"
                                    onClick={goHome}
                                    aria-label="На головну"
                                    title="На головну"
                                >
                                    <Home size={17} strokeWidth={2.4} aria-hidden="true" />
                                </button>
                            ) : null}
                            <div className="header-greeting-line">
                                {operatorName ? (
                                    <>Вітаю, <span>{operatorName}</span>!</>
                                ) : (
                                    <>Вітаю, операторе!</>
                                )}
                            </div>
                        </div>

                        {weatherText ? (
                            <div className="header-weather-row">
                                {serverNow ? `${showTime ? serverDateTimeText : serverDateText}  ` : ""}
                                {weatherText}
                            </div>
                        ) : null}

                        {(displayPageTitle || (!weatherText && serverNow)) && (
                            <div className="header-meta-row">
                                {displayPageTitle ? (
                                    <div className="header-page-title" title={displayPageTitle}>
                                        <span className="header-page-title__text">{displayPageTitle}</span>
                                    </div>
                                ) : null}

                                {!weatherText && serverNow ? (
                                    <div className="header-datetime">
                                        {showTime ? serverDateTimeText : serverDateText}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>

                <div className="navbar-container" ref={navbarRef}>
                    {topItems.map(({ key, name, icon }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => handleSectionClick(key)}
                            className={`nav-item ${selectedSection === key ? "active" : ""}`}
                        >
                            <span className="nav-icon">{icon}</span>
                            <span className="nav-text">{name}</span>
                        </button>
                    ))}

                    {menuGroups.map((group) => {
                        const isOpen = openGroupId === group.id;
                        const hasGroupDot =
                            (group.id === "infra" && pendingClientApprovalsCount > 0) ||
                            (group.id === "comm" && (hasChats || hasPolls || openRequestsCount > 0)) ||
                            (group.id === "content" && (hasNews || hasOperatorInfo));

                        return (
                            <div className="nav-group" key={group.id}>
                                <button
                                    type="button"
                                    className={`nav-group-button ${isOpen ? "open" : ""}`}
                                    onClick={() => setOpenGroupId((cur) => (cur === group.id ? null : group.id))}
                                    aria-expanded={isOpen}
                                >
                                    <span className="nav-group-caret" aria-hidden="true">{isOpen ? "▼" : "▶"}</span>
                                    <span className="nav-group-text">{group.title}</span>
                                    {hasGroupDot && <span className="nav-dot nav-dot--group" />}
                                </button>

                                {isOpen && (
                                    <div className="nav-group-panel" role="menu">
                                        {group.items.map(({ key, name, icon }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => handleSectionClick(key)}
                                                className={`nav-panel-button ${selectedSection === key ? "active" : ""}`}
                                                role="menuitem"
                                            >
                                                <span className="nav-icon">{icon}</span>
                                                <span className="nav-text">{name}</span>
                                                {getMenuBadgeCount(key) > 0 && <span className="nav-badge">{getMenuBadgeCount(key)}</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {bottomItems.map(({ key, name, icon }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => handleSectionClick(key)}
                            className="nav-item nav-item--single"
                        >
                            <span className="nav-icon">{icon}</span>
                            <span className="nav-text">{name}</span>
                        </button>
                    ))}
                </div>

                {/* Hamburger */}
                <button
                    className={`header-hamburger ${menuOpen ? "is-open" : ""}`}
                    onClick={() => setMenuOpen((open) => !open)}
                    aria-label="Меню"
                >
                    <span className="header-hamburger__line" />
                    <span className="header-hamburger__line" />
                    <span className="header-hamburger__line" />
                </button>
            </div>

            {/* Sidebar drawer for mobile */}
            <div className={`sidebar-drawer ${menuOpen ? "open" : ""}`} ref={drawerRef}>
                <div className="drawer-head">
                    <div className="drawer-head__copy">
                        <strong className="drawer-head__title">Меню</strong>
                        <span className="drawer-head__subtitle">Розділи оператора</span>
                    </div>
                    <div className="drawer-head__theme">
                        <ThemeSelector compact />
                    </div>
                </div>
                <div className="drawer-scroll">
                    <div className="drawer-menu">
                        {topItems.map(({ key, name, icon }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => handleSectionClick(key)}
                                className={`drawer-button ${selectedSection === key ? "active" : ""}`}
                            >
                                <span className="nav-icon">{icon}</span>
                                <span className="nav-text">{name}</span>
                            </button>
                        ))}

                        {menuGroups.map((group) => {
                            const isOpen = openGroupId === group.id;
                            const hasGroupDot =
                                (group.id === "infra" && pendingClientApprovalsCount > 0) ||
                                (group.id === "comm" && (hasChats || hasPolls || openRequestsCount > 0)) ||
                                (group.id === "content" && (hasNews || hasOperatorInfo));

                            return (
                                <div className="drawer-group" key={group.id}>
                                    <button
                                        type="button"
                                        className={`drawer-group-button ${isOpen ? "open" : ""}`}
                                        onClick={() => setOpenGroupId((cur) => (cur === group.id ? null : group.id))}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="drawer-group-caret" aria-hidden="true">{isOpen ? "▼" : "▶"}</span>
                                        <span className="drawer-group-text">{group.title}</span>
                                        {hasGroupDot && <span className="nav-dot nav-dot--group" />}
                                    </button>

                                    {isOpen && (
                                        <div className="drawer-group-items">
                                            {group.items.map(({ key, name, icon }) => (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    onClick={() => handleSectionClick(key)}
                                                    className={`drawer-button ${selectedSection === key ? "active" : ""}`}
                                                >
                                                    <span className="nav-icon">{icon}</span>
                                                    <span className="nav-text">{name}</span>
                                                    {getMenuBadgeCount(key) > 0 && <span className="nav-badge">{getMenuBadgeCount(key)}</span>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {bottomItems.map(({ key, name, icon }) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => handleSectionClick(key)}
                                className={`drawer-button ${key === "logout" ? "drawer-button--logout" : ""}`}
                            >
                                <span className="nav-icon">{icon}</span>
                                <span className="nav-text">{name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {menuOpen && <div className="drawer-backdrop" onClick={() => setMenuOpen(false)} />}

            <div className={`shell-bottom-dock ${menuOpen ? "is-menu-open" : ""}`} aria-label="Навігація">
                <button
                    type="button"
                    className="shell-bottom-dock__btn"
                    onClick={goHome}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    aria-label="На головну"
                    title="На головну"
                >
                    <Home size={18} strokeWidth={2.4} aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className={`shell-bottom-dock__btn shell-bottom-dock__btn--menu ${menuOpen ? "is-open" : ""}`}
                    onClick={() => setMenuOpen((open) => !open)}
                    onPointerUp={(e) => e.currentTarget.blur()}
                    aria-label={menuOpen ? "Закрити меню" : "Меню"}
                    aria-expanded={menuOpen}
                    title={menuOpen ? "Закрити меню" : "Меню"}
                >
                    <span className="header-hamburger__line" />
                    <span className="header-hamburger__line" />
                    <span className="header-hamburger__line" />
                </button>
            </div>

            {/* Main content */}
            <div
                className="main-content"
                onClick={() => {
                    if (menuOpen) setMenuOpen(false);
                }}
            >
                {!isLicenseInvalid && pendingClientApprovalsCount > 0 && selectedSection !== "clients" && (
                    <div className="message-banner pending-approvals-banner" role="status" aria-live="polite">
                        <div className="pending-approvals-banner__content">
                            <div>
                                <strong>Увага: {pendingClientApprovalsCount} нових клієнтських запитів на погодження</strong>
                                <div style={{ marginTop: 4, fontSize: 13 }}>
                                    Відкрий сторінку клієнтів і підтвердь доступ до сервісу / мережі.
                                </div>
                            </div>
                            <button type="button" className="add-btn" onClick={() => handleSectionClick("clients")}>
                                Відкрити клієнтів
                            </button>
                        </div>
                    </div>
                )}

                {showLicenseWarning && (
                    <div className="message-banner license-warning license-warning--sticky" role="status" aria-live="polite">
                        Увага! Термін ліцензії закінчується через <strong>{warningDaysLeft}</strong>{" "}
                        {warningDaysLeft === 1 ? "день" : warningDaysLeft < 5 ? "дні" : "днів"}.
                        Зверніться до адміністратора мережі для продовження. Це повідомлення зникне після продовження ліцензії.
                    </div>
                )}

                {isLicenseInvalid ? (
                    <div className="license-blocked-screen" role="alert" aria-live="assertive">
                        <div className="message-banner license-blocked">
                            <strong>Роботу заблоковано.</strong> {licenseStatus?.detail || "Термін ліцензії закінчився або ліцензія не встановлена."}
                            <div style={{ marginTop: 8 }}>
                                Зверніться до адміністратора мережі для продовження. Доступ відновиться автоматично після оновлення ліцензії.
                            </div>
                            <div style={{ marginTop: 14 }}>
                                <button
                                    type="button"
                                    className="add-btn"
                                    onClick={() => handleSectionClick("logout")}
                                >
                                    Вийти з акаунта
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {selectedSection === "dashboard" && (
                            <OperatorWidgets
                                onNavigate={handleSectionClick}
                                pendingClientApprovalsCount={pendingClientApprovalsCount}
                                hasNews={hasNews}
                                unreadNewsCount={unreadNewsCount}
                                hasOperatorInfo={hasOperatorInfo}
                                unreadOperatorInfoCount={unreadOperatorInfoCount}
                                hasChats={hasChats}
                                unreadChatsCount={unreadChatsCount}
                                hasPolls={hasPolls}
                                unreadPollsCount={unreadPollsCount}
                                openRequestsCountBadge={openRequestsCount}
                                sensorAccess={sensorAccess}
                                networkFeatures={networkFeatureFlags}
                            />
                        )}
                        {selectedSection === "locations" && <Locations />}
                        {selectedSection === "devices" && <Devices />}
                        {selectedSection === "sensors" && <OperatorSensors access={sensorAccess} />}
                        {selectedSection === "sockets" && <Sockets />}
                        {selectedSection === "door-access" && <DeviceAccessOperator />}
                        {selectedSection === "camera-access" && <CameraAccessOperator />}
                        {selectedSection === "news" && (
                            <News
                                onNewsViewed={() => {
                                    setHasNews(false);
                                    setUnreadNewsCount(0);
                                    refreshUnreadOperatorNews();
                                }}
                            />
                        )}
                        {selectedSection === "polls" && (
                            <OperatorPolls
                                onPollsViewed={() => {
                                    setHasPolls(false);
                                    setUnreadPollsCount(0);
                                    refreshUnreadOperatorPolls();
                                }}
                            />
                        )}
                        {selectedSection === "operator-info" && (
                            <OperatorInfo
                                onOperatorInfoViewed={() => {
                                    setHasOperatorInfo(false);
                                    setUnreadOperatorInfoCount(0);
                                    refreshOperatorInfoBadge();
                                }}
                            />
                        )}
                        {selectedSection === "chat" && <Chat
                            onUnreadChatsCountChange={(count) => {
                                setUnreadChatsCount(count);
                                setHasChats(count > 0);
                            }}
                            onShellNavChange={setChatShellNav}
                        />}
                        {selectedSection === "cars" && <Cars onNavigate={handleSectionClick} />}
                        {selectedSection === "clients" && (canSeeClients || pendingClientApprovalsCount > 0) && (
                            <Clients onPendingAccessRequestsChange={setPendingClientApprovalsCount} />
                        )}
                        {selectedSection === "requests" && <OperatorRequests />}
                        {selectedSection === "billing" && <OperatorBilling />}
                        {selectedSection === "client-portal" && (
                            <ClientPortalSettings networkFeatures={networkFeatureFlags} />
                        )}
                        {selectedSection === "profile" && <Profile />}
                        {selectedSection === "support" && <SupportOperator />}
                    </>
                )}
            </div>
        </>
    );
};

export default OperatorPage;
